import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { RootState } from '..';
import { SnowflakeParentType, SnowflakeConfigurationItem } from '@server/snowflake-documentation';

type ElementsByType = Partial<Record<SnowflakeParentType, SnowflakeConfigurationItem[]>>;

export const SNOWFLAKE_DEFAULT_TAB = SnowflakeParentType.SCHEMA;

export interface SnowflakeDocumentationState {
  elementsByOrg: {
    [crmOrgId: string]: ElementsByType;
  };
  loadingByOrg: {
    [crmOrgId: string]: Partial<Record<SnowflakeParentType, boolean>>;
  };
  activeParentType: SnowflakeParentType;
  openTabs: SnowflakeParentType[];
}

const initialState: SnowflakeDocumentationState = {
  elementsByOrg: {},
  loadingByOrg: {},
  activeParentType: SNOWFLAKE_DEFAULT_TAB,
  openTabs: [SNOWFLAKE_DEFAULT_TAB],
};

export const snowflakeDocumentationSlice = createSlice({
  name: 'snowflakeDocumentation',
  initialState,
  reducers: {
    setSnowflakeElements: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        parentType: SnowflakeParentType;
        elements: SnowflakeConfigurationItem[];
      }>,
    ) => {
      const { crmOrgId, parentType, elements } = action.payload;
      if (!state.elementsByOrg[crmOrgId]) {
        state.elementsByOrg[crmOrgId] = {};
      }
      state.elementsByOrg[crmOrgId][parentType] = elements;
    },

    setSnowflakeLoading: (
      state,
      action: PayloadAction<{
        crmOrgId: string;
        parentType: SnowflakeParentType;
        isLoading: boolean;
      }>,
    ) => {
      const { crmOrgId, parentType, isLoading } = action.payload;
      if (!state.loadingByOrg[crmOrgId]) {
        state.loadingByOrg[crmOrgId] = {};
      }
      state.loadingByOrg[crmOrgId][parentType] = isLoading;
    },

    clearSnowflakeElements: (state, action: PayloadAction<{ crmOrgId: string }>) => {
      delete state.elementsByOrg[action.payload.crmOrgId];
      delete state.loadingByOrg[action.payload.crmOrgId];
    },

    setSnowflakeActiveParentType: (state, action: PayloadAction<SnowflakeParentType>) => {
      state.activeParentType = action.payload;
    },

    addSnowflakeTab: (state, action: PayloadAction<SnowflakeParentType>) => {
      if (!state.openTabs.includes(action.payload)) {
        state.openTabs.push(action.payload);
      }
      state.activeParentType = action.payload;
    },

    removeSnowflakeTab: (state, action: PayloadAction<SnowflakeParentType>) => {
      if (action.payload === SNOWFLAKE_DEFAULT_TAB) return;
      state.openTabs = state.openTabs.filter((t) => t !== action.payload);
      if (state.activeParentType === action.payload) {
        state.activeParentType = state.openTabs[state.openTabs.length - 1] ?? SNOWFLAKE_DEFAULT_TAB;
      }
    },
  },
});

export const {
  setSnowflakeElements,
  setSnowflakeLoading,
  clearSnowflakeElements,
  setSnowflakeActiveParentType,
  addSnowflakeTab,
  removeSnowflakeTab,
} = snowflakeDocumentationSlice.actions;

export const selectSnowflakeElements =
  (crmOrgId: string, parentType: SnowflakeParentType) => (state: RootState) =>
    state[snowflakeDocumentationSlice.name]?.elementsByOrg[crmOrgId]?.[parentType];

export const selectSnowflakeLoading =
  (crmOrgId: string, parentType: SnowflakeParentType) => (state: RootState) =>
    state[snowflakeDocumentationSlice.name]?.loadingByOrg[crmOrgId]?.[parentType] ?? false;

export const selectAllSnowflakeElements = (crmOrgId: string) => (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.elementsByOrg[crmOrgId];

export const selectSnowflakeActiveParentType = (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.activeParentType ?? SNOWFLAKE_DEFAULT_TAB;

export const selectSnowflakeOpenTabs = (state: RootState) =>
  state[snowflakeDocumentationSlice.name]?.openTabs ?? [SNOWFLAKE_DEFAULT_TAB];

export default snowflakeDocumentationSlice.reducer;
